import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormControl, MenuItem, Select } from '@mui/material';
import { BORDER_RADIUS, semanticColors } from '@/theme/tokens';
import { SUPPORTED_LANGUAGES, SupportedLanguageCode } from '@/constants/languages';

interface LanguageSelectorProps {
  value: SupportedLanguageCode;
  onChange: (code: SupportedLanguageCode) => void;
  /** Id of the visible label to link as the accessible name; falls back to a
   * generic ``aria-label`` when omitted. Keeps the announced name in step with
   * the on-screen label (WCAG 2.5.3). */
  labelId?: string;
  disabled?: boolean;
}

/**
 * Full-width language selection bar. The open dropdown mirrors the header
 * language chip (``HeaderLanguageSelector``) so the picker reads as part of
 * the design system rather than the browser's native select menu.
 */
const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  value,
  onChange,
  labelId,
  disabled,
}) => {
  const { t } = useTranslation('common');
  return (
    <FormControl size="small" fullWidth>
      <Select
        value={value}
        disabled={disabled}
        labelId={labelId}
        onChange={(e) => onChange(e.target.value as SupportedLanguageCode)}
        inputProps={labelId ? undefined : { 'aria-label': t('language') }}
        MenuProps={{
          PaperProps: {
            sx: {
              mt: '4px',
              borderRadius: BORDER_RADIUS,
              boxShadow: `0 4px 16px ${semanticColors.shadowLight}`,
            },
          },
        }}
        sx={{
          borderRadius: BORDER_RADIUS,
          fontSize: '14px',
          color: semanticColors.textBody,
          backgroundColor: semanticColors.surface,
          // Inset frame matching the header chip; ``inputBorderHover`` (#707070)
          // keeps it >=3:1 on white (WCAG).
          boxShadow: `inset 0 0 0 1px ${semanticColors.inputBorderHover}`,
          transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
          '& .MuiSelect-icon': { color: semanticColors.textTertiary },
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          '&:hover': {
            backgroundColor: semanticColors.surfaceSubtle,
            boxShadow: `inset 0 0 0 1px ${semanticColors.textTertiary}`,
          },
          '&.Mui-focusVisible, &:has(:focus-visible)': {
            boxShadow: `inset 0 0 0 1px ${semanticColors.brandPrimary}, 0 0 0 2px ${semanticColors.brandPrimary}`,
          },
        }}
      >
        {/* `lang` per option so a screen reader pronounces each endonym in its
            own language instead of reading 简体中文 with an English voice. */}
        {SUPPORTED_LANGUAGES.map(({ code, label }) => (
          <MenuItem key={code} value={code} lang={code} sx={{ fontSize: '14px' }}>
            {label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default LanguageSelector;
